import {assert} from 'Asserts';
import {warn} from 'util/Logging';

const DATA_CHANNEL_NAME = 'oai-events';

/**
 * WebRTC session manager for realtime voice communication with an LLM.
 *
 * The initial target is the OpenAI Realtime API, see docs and code samples here:
 * https://platform.openai.com/docs/guides/realtime-webrtc
 *
 * This class should encapsulate the DOM-specific components, including audio playback and microphone
 * input. It is mocked for testing, so also should isolate anything DOM-related and avoid other business
 * logic.
 *
 * Usage:
 * const session = await WebRTCSession.create();
 * const sdpOffer = await session.createSDPOffer();
 * await session.startSessionWithSDPAnswer([get SDP answer based on offer]);
 * ...
 * session.close();
 */
export default class WebRTCSession {
  private wakeLock: WakeLockSentinel | null = null;

  private constructor(
    private readonly peerConnection: RTCPeerConnection,
    private readonly dataChannel: RTCDataChannel,
    private readonly microphoneStream: MediaStream,
    private readonly audioElement: HTMLAudioElement
  ) {}

  /**
   * Create and initialize a new WebRTC session.
   */
  static async create(): Promise<WebRTCSession> {
    const peerConnection = new RTCPeerConnection();
    const dataChannel = peerConnection.createDataChannel(DATA_CHANNEL_NAME);

    const audioElement = document.createElement('audio');
    audioElement.autoplay = true;
    peerConnection.ontrack = (event) => {
      audioElement.srcObject = event.streams[0];
    };

    const microphoneStream = await navigator.mediaDevices.getUserMedia({audio: true});
    for (const track of microphoneStream.getTracks()) {
      peerConnection.addTrack(track);
    }

    return new WebRTCSession(peerConnection, dataChannel, microphoneStream, audioElement);
  }

  /**
   * Create an SDP offer for this session.
   */
  async createSDPOffer(): Promise<string> {
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    const sdp = offer.sdp;
    assert(sdp != null, 'Unexpectedly received null SDP');
    return sdp;
  }

  /**
   * Start the session based on the SDP answer received from the server.
   */
  async startSessionWithSDPAnswer(sdpAnswer: string): Promise<void> {
    await this.peerConnection.setRemoteDescription({
      type: 'answer',
      sdp: sdpAnswer,
    });
    await this.acquireWakeLock();
  }

  /**
   * Acquire a wake lock to keep the screen awake during the session.
   */
  private async acquireWakeLock(): Promise<void> {
    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
    } catch (err) {
      if (err instanceof DOMException) {
        // The wake lock may be refused, e.g. on low battery. Ignore the error in that case.
        warn(`Failed to acquire wake lock: ${err.message}`);
      } else {
        throw err;
      }
    }
  }

  /**
   * Close the session and clean up all resources.
   */
  async close(): Promise<void> {
    this.peerConnection.close();
    this.dataChannel.close();
    for (const track of this.microphoneStream.getTracks()) {
      track.stop();
    }
    this.audioElement.pause();
    await this.wakeLock?.release();
  }
}
